import { ButtonDS, DialogModalDS, InputDS, LoaderOverlay, Spacer } from "@qivia/ui";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import "../../../i18n/i18n";
import {
  connectDevicesToProviderAsync,
  selectConnectDevicesToProviderStatus,
} from "../telemetrySlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";

export type VehicleToInstallDevice = {
  uuid: string;
  registrationNumber: string;
};

type InstallDeviceModalProps = {
  visible: boolean;
  onClose: () => void;
  vehicle: VehicleToInstallDevice;
};

export const InstallDeviceModal = ({
  visible,
  onClose,
  vehicle,
}: InstallDeviceModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const connectDevicesToProviderStatus = useAppSelector(
    selectConnectDevicesToProviderStatus,
  );
  const [deviceId, setDeviceId] = useState<string>("");
  const [displayError, setDisplayError] = useState<boolean>(false);

  const isProcessing = useMemo(
    () => connectDevicesToProviderStatus === "processing",
    [connectDevicesToProviderStatus],
  );

  const handleClose = useCallback(() => {
    setDeviceId("");
    setDisplayError(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (!deviceId) {
      setDisplayError(true);
      return;
    }

    if (!params.id) {
      return;
    }

    void dispatch(
      connectDevicesToProviderAsync({
        data: [
          {
            vehicleUuid: vehicle.uuid,
            registrationNumber: vehicle.registrationNumber,
            deviceId,
          },
        ],
        companyUuid: params.id,
      }),
    );
  }, [deviceId, dispatch, vehicle, params.id]);

  useEffect(() => {
    if (
      connectDevicesToProviderStatus === "success" ||
      connectDevicesToProviderStatus === "failed"
    ) {
      handleClose();
    }
  }, [connectDevicesToProviderStatus, handleClose]);

  if (!visible) return null;

  return (
    <DialogModalDS
      visible={visible}
      onClose={handleClose}
      title={t("telemetry.installDeviceModal.title")}
      width={35}
      deactivateClickOutsideSidePanel={isProcessing}
      bottomButton={
        <>
          <ButtonDS
            text={t("telemetry.installDeviceModal.cancelButton")}
            format="fill"
            buttonType="secondary"
            onClick={handleClose}
            disabled={isProcessing}
          />
          <Spacer x={1} />
          <ButtonDS
            text={t("telemetry.installDeviceModal.confirmButton")}
            format="fill"
            buttonType="primary"
            onClick={handleSubmit}
            disabled={!deviceId || isProcessing}
          />
        </>
      }
    >
      <StyledContent>
        <LoaderOverlay visible={isProcessing} />
        <InputDS
          label={t("telemetry.installDeviceModal.input.deviceId.label")}
          placeholder={t(
            "telemetry.addDevicesToProvider.input.deviceId.placeholder",
          )}
          value={deviceId}
          onChange={(e) => {
            setDeviceId(e.target.value);
            setDisplayError(false);
          }}
          error={
            displayError && !deviceId
              ? t("telemetry.installDeviceModal.input.deviceId.error")
              : undefined
          }
        />
      </StyledContent>
    </DialogModalDS>
  );
};

const StyledContent = styled.div`
  position: relative;
`;
